import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Video,
  HeartHandshake,
  ArrowRight,
  Phone,
  Sparkles,
} from "lucide-react";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const Acompanamiento = () => {
  const { t } = useLanguage();
  const [offerings, setOfferings] = useState([]);
  useEffect(() => {
    let active = true;
    api.get("/api/acompanamiento/catalog").then(({ data }) => {
      if (active) setOfferings(data);
    }).catch(() => { if (active) setOfferings([]); });
    return () => { active = false; };
  }, []);

  return (
    <div className="min-h-screen" data-testid="acompanamiento-page">
      <PublicNav />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10">
        <div
          className="font-mont text-[11px] tracking-[0.45em] uppercase mb-5"
          style={{ color: "var(--kd9-accent)" }}
        >
          · {t("acomp.kicker")} ·
        </div>
        <h1
          className="font-cinzel kd9-gold-text mb-4 leading-[1.05]"
          style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", fontWeight: 700, letterSpacing: "0.04em" }}
          data-testid="acomp-title"
        >
          {t("acomp.title").toUpperCase()}
        </h1>
        <p
          className="font-cormorant text-xl max-w-2xl"
          style={{ color: "var(--kd9-text-soft)" }}
        >
          {t("acomp.subtitle")}
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {offerings.map((item) => {
            const Icon = item.session_type === "phone" ? Phone : Video;
            return <article key={item.id} className="kd9-card p-7">
              <Icon size={24} style={{ color: "var(--kd9-gold)" }}/>
              <h2 className="font-cinzel kd9-gold-text text-xl mt-4">{item.name}</h2>
              <p className="font-cormorant text-lg my-3" style={{ color: "var(--kd9-text-soft)" }}>{item.description}</p>
              <div className="font-mont text-xs mb-5">{item.duration_min} min · {item.is_free ? "Gratuito · 0 KD" : `${new Intl.NumberFormat("es-CO").format(item.energy_cost)} Energía KD9`}</div>
              <Link to="/register" className="kd9-btn-primary px-5 py-3 rounded-lg text-[11px] inline-flex items-center gap-2">Solicitar <ArrowRight size={14}/></Link>
            </article>;
          })}
        </div>
        {!offerings.length && <div className="kd9-card p-8 text-center font-mont">No hay acompañamientos publicados actualmente.</div>}
      </section>

      <Footer />
    </div>
  );
};

/** Reusable section so Landing can embed the same acompañamiento block above academia */
export const AcompSection = ({ options, t, embedded = false }) => (
  <section
    className={`max-w-7xl mx-auto px-6 lg:px-10 ${embedded ? "py-16" : "pb-20"}`}
    data-testid="acompanamiento-section"
  >
    {embedded && (
      <div className="text-center mb-12">
        <div className="font-mont text-[11px] tracking-[0.45em] uppercase mb-3" style={{ color: "var(--kd9-accent)" }}>
          · {t("acomp.kicker")} · {t("section.acompanamiento")}
        </div>
        <h2 className="font-cinzel kd9-gold-text text-3xl md:text-5xl tracking-[0.06em] mb-3">
          {t("acomp.title").toUpperCase()}
        </h2>
        <p className="font-cormorant text-xl max-w-2xl mx-auto" style={{ color: "var(--kd9-text-soft)" }}>
          {t("acomp.subtitle")}
        </p>
      </div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
      {options.map((opt, i) => (
        <article
          key={opt.key}
          className={`kd9-card overflow-hidden kd9-fade-up kd9-delay-${(i % 6) + 1}`}
          data-testid={`acomp-card-${opt.key}`}
        >
          <div className="grid grid-cols-[160px_1fr] sm:grid-cols-[200px_1fr]">
            <div className="relative">
              <img src={opt.img} alt={t(`acomp.${opt.key}`)} className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{
                background: `linear-gradient(135deg, ${opt.color}88 0%, ${opt.color}22 60%, transparent 100%)`,
              }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center backdrop-blur"
                  style={{
                    background: "rgba(0,0,0,0.35)",
                    border: `1px solid ${opt.color}aa`,
                    boxShadow: `0 0 28px -8px ${opt.color}`,
                  }}
                >
                  <opt.icon size={24} style={{ color: "#fff" }} />
                </div>
              </div>
            </div>
            <div className="p-6 flex flex-col">
              <h3 className="font-cinzel kd9-gold-text text-xl tracking-[0.08em] mb-2">
                {t(`acomp.${opt.key}`).toUpperCase()}
              </h3>
              <p
                className="font-cormorant text-lg mb-5 flex-1"
                style={{ color: "var(--kd9-text-soft)" }}
              >
                {t(`acomp.${opt.key}.desc`)}
              </p>
              <Link
                to="/register"
                className="kd9-btn-primary px-5 py-3 rounded-lg text-[11px] inline-flex items-center gap-2 self-start"
                data-testid={`acomp-cta-${opt.key}`}
              >
                {t("diag.cta")} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </article>
      ))}
    </div>

    {/* big human reassurance */}
    {!embedded && (
      <div
        className="kd9-card mt-10 p-10 lg:p-14 text-center"
        data-testid="acomp-reassurance"
      >
        <HeartHandshake
          size={42}
          className="mx-auto mb-5"
          style={{ color: "var(--kd9-success)" }}
        />
        <p
          className="font-cormorant italic text-2xl max-w-2xl mx-auto mb-6"
          style={{ color: "var(--kd9-text)" }}
        >
          &ldquo;Detrás de cada diagnóstico hay una persona real escuchándote. No vendemos respuestas — construimos procesos contigo.&rdquo;
        </p>
        <Link
          to="/contacto"
          className="kd9-btn-primary px-7 py-4 rounded-xl text-[12px] inline-flex items-center gap-3"
        >
          <Sparkles size={14} /> Empezar mi acompañamiento
        </Link>
      </div>
    )}
  </section>
);

export default Acompanamiento;
